import { map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';

import { BackgroundJob } from '../Model/Dto/BackgroundJob';
import { StartAction } from '../Model/Dto/StartAction';
import { TranslateService } from '@ngx-translate/core';
import { RestartJob } from '../Model/Dto/RestartJob';
import { ValidateExportJob } from '../Model/Dto/ValidateExportJob';
import { BackgroundJobEvent } from '../Model/Dto/BackgroundJobEvent';
import { SendCompareExportMail } from '../Model/Dto/SendCompareExportMail';
import { ExportParameter } from '../Model/exportParameter/ExportParameter';
import { Macro } from 'app/Model/ui/Macro';
import { StringResponse } from '../Model/Dto/StringResponse';

@Injectable()
export class JobService {
  constructor(
    private http: HttpClient,
    public translate: TranslateService
  ) {
    
  }

  delete(id) {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', id)
    };

    this.http.delete('api/job/delete', options).subscribe(
      (result) => {},
      (err) => console.error(err)
    );
  }

  deleteFinishedJobs(customerId: string) {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId)
    };
    return this.http.delete('api/job/deleteFinishedJobs', options);
  }

  sendCompareExportMail(dto: SendCompareExportMail): Promise<object> {
    return lastValueFrom(this.http.post('api/Job/SendCompareExportMail', dto));
  }

  cancelJob(id): Promise<object> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', id)
    };
    return lastValueFrom(this.http.post('api/job/CancelJob', null, options));
  }

  setIsDeleted(id): Promise<StringResponse> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', id)
    };
    return lastValueFrom(this.http.post<StringResponse>('api/job/SetIsDeleted', null, options));
  }

  setIsDeletedForFinishedJobs(customerId: string) {
    let i = 1;

    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId)
    };
    return this.http.post('api/job/SetIsDeletedForFinishedJobs', null, options);
  }

  setIsDeletedForSelectedJobs(jobIds: Array<number>) {
    let i = 1;

    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json')
    };
    return this.http.post('api/job/SetIsDeletedForSelectedJobs', jobIds, options);
  }

  setIsPinned(jobId: number, remarks: string, pinExpires: Date) {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', jobId.toString()).append('remarks', remarks)
    };

    return this.http.post('api/Job/SetIsPinned', pinExpires, options).pipe(
      map((response) => {
        return true;
      })
    );
  }

  setIsNotPinned(jobId: number) {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', jobId.toString())
    };

    return this.http.post('api/Job/SetIsNotPinned', null, options).pipe(
      map((response) => {
        return true;
      })
    );
  }

  getExportJobParameter(jobId: number, customerId: string): Observable<ExportParameter> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', jobId).append('customerId', customerId)
    };
    return this.http.get<ExportParameter>('api/job/GetExportJobParameter', options) as any;
  }

  getJobs(customerId: string): Observable<BackgroundJob[]> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId)
    };
    return this.http.get<BackgroundJob[]>('api/job/GetJobs', options) as any;
  }

  getJobById(customerId: string, jobId: string): Promise<BackgroundJob> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId).append('jobId', jobId)
    };
    return lastValueFrom(this.http.get<BackgroundJob>('api/job/GetJobById', options));
  }

  isRefreshJobRunningInCatalog(catalogId: string, customerId: string): Observable<boolean> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('catalogId', catalogId).append('customerId', customerId)
    };
    return this.http.get<BackgroundJob[]>('api/job/IsRefreshJobRunningInCatalog', options) as any;
  }

  isJobRunningInCatalog(catalogId: string, customerId: string): Observable<boolean> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('catalogId', catalogId).append('customerId', customerId)
    };
    return this.http.get<BackgroundJob[]>('api/job/IsJobRunningInCatalog', options) as any;
  }

  isJobRunningThatChangesCatalogList(customerId: string): Observable<boolean> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId)
    };
    return this.http.get<BackgroundJob[]>('api/job/IsJobRunningThatChangesCatalogList', options) as any;
  }

  isJobRunningThatChangesWawiList(customerId: string): Observable<boolean> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId)
    };
    return this.http.get<BackgroundJob[]>('api/job/IsJobRunningThatChangesWawiList', options) as any;
  }

  isJobRunningThatChangesDatasourceList(customerId: string): Promise<boolean> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId)
    };
    return lastValueFrom(this.http.get<boolean>('api/job/IsJobRunningThatChangesDatasourceList', options));
  }

  getJobEvents(jobId: number): Observable<BackgroundJobEvent[]> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('jobId', jobId.toString())
    };
    return this.http.get<BackgroundJobEvent[]>('api/job/GetJobEvents', options) as any;
  }

  getJobsByCatalogId(catalogId: string, onlyNotDeleted: boolean): Observable<BackgroundJob[]> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('catalogId', catalogId).append('onlyNotDeleted', onlyNotDeleted.toString())
    };
    return this.http.get<BackgroundJob[]>('api/job/GetJobsByCatalogId', options) as any;
  }

  startUserAction(startAction: StartAction): Observable<number | null> {
    return this.http.post('api/Job/StartCatalogAction', startAction).pipe(
      map((response: any) => {
        const jobId = parseInt(response, 10);
        return isNaN(jobId) ? null : jobId;
      })
    );
  }

  startJob(startAction: StartAction) {
    const result = this.http.post('api/Job/StartJob', startAction).pipe(
      map((response) => {
        return true;
      })
    );
    return result;
  }

  restartJob(dto: RestartJob) {
    return this.http.post('api/Job/RestartJob', dto).pipe(
      map((response) => {
        return true;
      })
    );
  }

  validateExportJob(dto: ValidateExportJob) {
    return this.http.post('api/Job/ValidateExportJob', dto).pipe(
      map((response) => {
        return true;
      })
    );
  }

  static getIconClass(icon) {
    switch (icon) {
      case 'Undefined':
        return 'dx-icon-preferences';
      case 'MissingTemplate':
        return 'dx-icon-preferences';
      case 'FileContentNotValid':
        return 'dx-icon-preferences';
      case 'WrongVersion':
        return 'dx-icon-preferences';
      case 'WrongFormat':
        return 'dx-icon-preferences';
      case 'Error':
        return 'dx-icon-warning';
      case 'Finished':
      case 'Done':
        return 'dx-icon-todo';
      case 'Running':
        return 'dx-icon-runner';
      case 'Info':
        return 'dx-icon-chevronnext';
    }
    return 'dx-icon-chevronnext';
  }

  getMacros(customerId: string, currentStage:string): Promise<Macro[]> {
    const options = {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
      params: new HttpParams().append('customerId', customerId).append('currentStage', currentStage)
    };
    return lastValueFrom(this.http.get<Macro[]>('api/job/GetMacros', options));
  }
}
